import React, { useState, useEffect } from "react";
import {
  Box,
  Text,
  Button,
  Flex,
  useToast, useColorModeValue, useTheme
} from "@chakra-ui/react";
import { cqAPI, Auth } from "../../lib/Auth";
import moment from "moment";

const PatientDiscountPlan = () => {
  const [plans, setPlans] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const toast = useToast();
  const bgColor = useColorModeValue( '#FFFFFF', '#212330');
  const theme = useTheme();
  const subBgColor = useColorModeValue( 'green.100', theme.colors.brand[20]);

  const fetchPlans = async () => {
    try {
      const response = await cqAPI.get("/settings/discountplans");
      setPlans(response.data.plans || []);
      setSubscriptions(response.data.subscriptions || []);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch discount plans.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    fetchPlans();
  }, [toast]);

  // Find the currently selected plan
  const currentPlan = subscriptions.find(
    (subscription) => !subscription.canceled_at
  );

  // Helper function to determine if a plan is an upgrade
  const isUpgrade = (plan) => {
    if (!currentPlan) return true; // If no current plan, all plans are valid
    const currentPlanPrice = currentPlan.plan.amount;
    const currentCoverageLength = Array.isArray(currentPlan.plan.coverage) ? currentPlan.plan.coverage.length : 0;

    return (
      plan.price > currentPlanPrice ||
      (Array.isArray(plan.coverage) ? plan.coverage.length : 0) > currentCoverageLength
    );
  };

  // Convert annual prices to monthly equivalents for sorting
  const getMonthlyPrice = (plan) => {
    return plan.interval === "year" ? plan.price / 12 : plan.price;
  };

  // Filter to get the plans that should be shown as upgrades or selectable
  let filteredPlans = plans.filter((plan) => {
    const selected = subscriptions.some(
      (subscription) => subscription.plan.id === plan.plan_id && !subscription.canceled_at
    );

    if (plan.disabled && !selected) return false; // Do not show disabled plans unless selected

    // Show the plan if it's either the selected one or an upgrade or if no plan is currently selected
    return selected || isUpgrade(plan);
  });

  // Sort the filtered plans by their monthly price
  filteredPlans = filteredPlans.sort((a, b) => getMonthlyPrice(a) - getMonthlyPrice(b));

  const handlePlanSelect = async (planId) => {
    const response = await cqAPI.post("/settings/discountplans", { plan: planId });
    if (response.type === "error") {
      toast({
        title: "Error",
        description: response.msg,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
    if (response.type === 'ok'){
      toast({
        title: "Success",
        description: response.msg || "Plan selected successfully.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      setSelectedPlan(null);
    }
  };

  const handleCancelPlan = async (subscriptionId) => {
    const response = await cqAPI.del("/settings/discountplans", {
      subscription_id: subscriptionId
    });

    if (response.type === "error") {
      toast({
        title: "Error",
        description: response.msg,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } else if (response.type === 'ok'){
      toast({
        title: "Success",
        description: response.msg || "Plan canceled successfully.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      fetchPlans()
    }
  };
  

  return (
    <Box
      p={4}
      overflowY="auto"
      width="100%"
      minHeight="100vh"
    >
      <Flex
        direction="column"
      >
        {filteredPlans.length > 0 &&
          filteredPlans.map((plan, index) => {
            const selectedSubscription = subscriptions.find(
              (subscription) => subscription.plan.id === plan.plan_id && !subscription.canceled_at
            );

            const coverage = Array.isArray(plan.coverage) ? plan.coverage : [];
            const accepting = Array.isArray(plan.bids) ? plan.bids : [];

            return (
              <Flex
                key={index}
                direction="column"
                alignItems="flex-start"
                p={4}
                my={2}
                ml={2}
                width="100%"
                borderWidth="1px"
                borderRadius="16px"
                bg={selectedSubscription ? subBgColor : bgColor}
              >
                <Box mb={2} width="100%">
                  <Text fontWeight="bold" fontSize="lg">
                    {plan.visible_name}{selectedSubscription? ` member since ${moment((selectedSubscription.metadata.start_date || selectedSubscription.created) * 1000).format('L')} (${selectedSubscription.status})` : ''}
                  </Text>
                  <Text>
                    ${plan.price} ({plan.interval === "year" ? "annually" : "monthly"})
                  </Text>
                </Box>

                <Text fontWeight="bold" mb={2}>
                  Coverage:
                </Text>
                <Box mb={4} textAlign="left">
                  {coverage.map((item, i) => (
                    <Text key={i}>{item}</Text>
                  ))}
                </Box>

                <Flex justify="space-between" width="100%">
                  {selectedSubscription ? (
                    <Button
                      colorScheme="red"
                      size="sm"
                      isDisabled={!Auth.user.isSuperAdmin}
                      onClick={() => {
                        setSelectedPlan(plan);
                        handleCancelPlan(selectedSubscription?.id)
                      }}
                    >
                      Cancel Plan
                    </Button>
                  ) : (
                    <Button
                      colorScheme="green"
                      size="sm"
                      onClick={() => handlePlanSelect(plan.plan_id)}
                    >
                      Select Plan
                    </Button>
                  )}
                </Flex>
              </Flex>
            );
          })}
      </Flex>
    </Box>
  );
};

export default PatientDiscountPlan;
